import React from 'react';
import { Icon } from './AdminShell';

/*
 * Tabel data — arketipe yang dipakai sekitar 15 dari 33 layar dashboard baru.
 *
 * Dibuat sekali di sini supaya layar berikutnya cuma perlu memberi kolom dan
 * sumber datanya. Itu sebabnya komponen ini agak besar sementara Live jobs di
 * atasnya cuma beberapa puluh baris.
 *
 * PAGINASI DAN PENCARIAN DI SERVER, bukan di klien. Resolver yang ada
 * (getAllBookings dan saudaranya) sudah menerima `currentPage` dan `searchList`
 * dan mengembalikan `count` total; mengambil semuanya lalu menyaring di
 * peramban akan membuang paginasi yang sudah bekerja dan pecah begitu tabelnya
 * lebih panjang dari satu layar.
 *
 * PENGURUTAN di klien, dan sengaja hanya untuk halaman yang sedang tampil —
 * resolver-nya tidak menerima parameter urutan. Kalau tidak dibatasi seperti
 * ini, "urutkan menurut tarif" akan terlihat seolah mengurutkan seluruh data
 * padahal cuma sepuluh baris yang kebetulan terbuka. Karena itu ada catatan
 * kecil di kaki tabel yang mengatakannya apa adanya.
 */

const Empty = ({ text, hint }) => (
  <div className="empty">
    <span className="ei">
      <svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
    </span>
    <b>{text}</b>
    {hint && <p style={{ marginTop: 6, fontSize: 'var(--fs-sm)' }}>{hint}</p>}
  </div>
);

class DataTable extends React.Component {
  state = { search: '', sort: null, dir: 'asc' };

  // Ketikan tidak boleh langsung memukul server tiap huruf.
  onSearch = e => {
    const search = e.target.value;
    this.setState({ search });
    clearTimeout(this.t);
    this.t = setTimeout(() => this.props.onSearch(search), 350);
  };

  componentWillUnmount() { clearTimeout(this.t); }

  toggleSort = key => {
    this.setState(s => ({
      sort: key,
      dir: s.sort === key && s.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  /*
   * Jendela lima nomor halaman yang bergeser mengikuti halaman aktif — sama
   * seperti `nums` di ui.js paket client. Tanpa ini, 14 halaman provider
   * berarti 14 tombol berjejer.
   */
  pageNumbers(page, pages) {
    const to = Math.min(pages, Math.max(1, page - 2) + 4);
    const from = Math.max(1, to - 4);
    const out = [];
    for (let i = from; i <= to; i++) out.push(i);
    return out;
  }

  sorted(rows) {
    const { sort, dir } = this.state;
    if (!sort) return rows;
    const col = this.props.columns.find(c => c.key === sort);
    const val = r => (col && col.sortValue ? col.sortValue(r) : r[sort]);
    return rows.slice().sort((a, b) => {
      const x = val(a), y = val(b);
      if (x === y) return 0;
      const n = (typeof x === 'number' && typeof y === 'number') ? x - y : String(x).localeCompare(String(y));
      return dir === 'asc' ? n : -n;
    });
  }

  render() {
    const {
      columns, rows, count, page, perPage = 10, onPage, onPerPage, loading,
      searchPlaceholder = 'Search…', filters, emptyText, emptyHint, toolbarExtra, copy
    } = this.props;
    const { search, sort, dir } = this.state;
    const pages = Math.max(1, Math.ceil((count || 0) / perPage));
    const list = this.sorted(rows || []);

    return (
      <section className="card">
        <div className="toolbar">
          <div className="search">
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input className="inp" value={search} onChange={this.onSearch}
              placeholder={searchPlaceholder} aria-label={searchPlaceholder} />
          </div>
          {filters}
          {toolbarExtra && <div className="sp">{toolbarExtra}</div>}
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {columns.map(c => {
                  const on = sort === c.key;
                  return (
                    <th key={c.key}
                      className={`${c.align === 'end' ? 'right ' : ''}${c.sortable ? 'sortable' : ''}`.trim() || null}
                      aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : null}
                      onClick={c.sortable ? () => this.toggleSort(c.key) : null}>
                      {c.label}
                      {c.sortable && <span className="ar">{on ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                // Baris kerangka, bukan spinner: tabel tidak berubah tinggi saat
                // datanya tiba, jadi halamannya tidak melompat.
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`s${i}`}>
                    {columns.map(c => (
                      <td key={c.key}><span className="skeleton" style={{ width: '70%' }} /></td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && list.map((r, i) => (
                <tr key={r.id || i}>
                  {columns.map(c => (
                    <td key={c.key}
                      className={c.key === 'act' ? 'acts' : (c.align === 'end' ? 'right' : null)}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && list.length === 0 && (
            <Empty text={emptyText || (copy && copy.nothingShow) || 'Nothing to show'} hint={emptyHint} />
          )}
        </div>

        {!!count && (pages > 1 || onPerPage) && (
          <div className="pager">
            <span>
              {copy
                ? copy.showing((page - 1) * perPage + 1, Math.min(page * perPage, count), count)
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, count)} of ${count.toLocaleString('en-US')}`}
            </span>
            {sort && (
              // Jujur soal batasnya: yang diurutkan cuma halaman ini.
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                {copy ? copy.sortedPage : 'sorted within this page'}
              </span>
            )}
            <div className="sp">
              <button className="pg-btn" type="button" disabled={page <= 1}
                onClick={() => onPage(page - 1)} aria-label={copy ? copy.previousPage : 'Previous page'}>
                <Icon name={copy && copy.rtl ? 'chevR' : 'chevL'} />
              </button>
              {this.pageNumbers(page, pages).map(p => (
                <button className="pg-btn" type="button" key={p}
                  aria-current={p === page ? 'true' : null}
                  onClick={() => onPage(p)}>{p}</button>
              ))}
              <button className="pg-btn" type="button" disabled={page >= pages}
                onClick={() => onPage(page + 1)} aria-label={copy ? copy.nextPage : 'Next page'}>
                <Icon name={copy && copy.rtl ? 'chevL' : 'chevR'} />
              </button>
              {onPerPage && (
                <select className="inp" value={perPage} aria-label={copy ? copy.rowsPerPage : 'Rows per page'}
                  onChange={e => onPerPage(Number(e.target.value))}>
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{copy ? copy.perPage(n) : `${n} / page`}</option>)}
                </select>
              )}
            </div>
          </div>
        )}
      </section>
    );
  }
}

export default DataTable;
